/**
 * Profile — Access card
 *
 * Presentational: role + workspace + groups. Controlled by
 * ProfileInfoSection, which owns the form state, the resolved workspace,
 * and the group selection. Rendered only when the viewer can see at least
 * one of these fields (guard lives in the parent).
 */

import { Group, Login, UserRole } from '../../../apis/api-client';
import GroupAutocomplete from '../../common/GroupAutocomplete';
import WorkspaceAutocomplete from '../../common/WorkspaceAutocomplete';
import { SettingsCard } from '../../common';
import { Box, Grid, SelectField, Typography } from '../../ui';

interface ProfileAccessCardProps {
  user: Login;
  formData: {
    role: any;
    workspaceId: number | undefined | null;
  };
  validationErrors: any;
  canEdit: boolean;
  canSeeRole: boolean;
  canEditRole: boolean;
  availableRoleOptions: any[];
  canSeeWorkspace: boolean;
  canSeeGroups: boolean;
  resolvedWorkspace: any;
  onWorkspaceChange: (ws: any) => void;
  selectedGroups: Group[];
  onGroupsChange: (groups: Group[]) => void;
  groupsLoading: boolean;
  onChange: (field: string, value: any) => void;
}

export default function ProfileAccessCard({
  user,
  formData,
  validationErrors,
  canEdit,
  canSeeRole,
  canEditRole,
  availableRoleOptions,
  canSeeWorkspace,
  canSeeGroups,
  resolvedWorkspace,
  onWorkspaceChange,
  selectedGroups,
  onGroupsChange,
  groupsLoading,
  onChange,
}: ProfileAccessCardProps) {
  return (
    <SettingsCard title="Access">
      <Grid container spacing={2}>
        {canSeeRole && (
          <Grid size={{ xs: 12, sm: 6 }}>
            {canEditRole ? (
              <SelectField
                label="Role"
                fullWidth
                value={formData.role}
                onChange={(e) => onChange('role', Number(e.target.value))}
                options={availableRoleOptions}
                allowEmpty={false}
                disabled={availableRoleOptions.length === 1}
              />
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" gutterBottom display="block">
                  Role
                </Typography>
                <Typography variant="body2">
                  {availableRoleOptions.find((r) => r.value === user.role)?.label || 'Learner'}
                </Typography>
              </Box>
            )}
          </Grid>
        )}
        {/* Spacer fills the right column on the Role row so the
            pair below starts cleanly on a fresh row. Only added
            when Role is visible AND the pair below WOULD render. */}
        {canSeeRole && (canSeeWorkspace || canSeeGroups) && (
          <Grid size={{ xs: 12, sm: 6 }} />
        )}
        {canSeeWorkspace && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <WorkspaceAutocomplete
              value={resolvedWorkspace}
              onChange={(ws) => onWorkspaceChange(ws)}
              size="medium"
              fullWidth
              required={formData.role === UserRole.WorkspaceAdmin}
              label="Workspace"
              placeholder="No Workspace"
              error={!!validationErrors.workspaceId}
              helperText={
                validationErrors.workspaceId ||
                (formData.role === UserRole.WorkspaceAdmin
                  ? 'Required for Workspace Admin role'
                  : undefined)
              }
            />
          </Grid>
        )}
        {canSeeGroups && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <GroupAutocomplete
              multiple
              value={selectedGroups}
              onChange={(v) => onGroupsChange((v as Group[]) ?? [])}
              workspaceId={formData.workspaceId ?? undefined}
              size="medium"
              fullWidth
              disabled={!canEdit || groupsLoading}
              label="Groups"
              placeholder={groupsLoading ? 'Loading groups…' : 'Search workspace groups…'}
              helperText="Workspace groups this user belongs to."
            />
          </Grid>
        )}
      </Grid>
    </SettingsCard>
  );
}
